import { Text } from "@mantine/core";

import { useSession } from "@/hooks/@custom/useSession";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { Role } from "@/store";
import { cn } from "@/utils/cn";
import { cva } from "@/utils/cva.config";

type QueueStatusKey = "scheduled" | "courtyard" | "attended" | "missing" | "tons" | "dropout" | "default";
type ModalKey = Extract<QueueStatusKey, "attended" | "missing" | "dropout">;

export interface ServiceStatusBox {
  key: ModalKey | string;
  title: string;
  value: number | string;
  isVisible: boolean;
  onMouseEnter?: () => void;
}

interface ExpeditionQueueStatusBoxProps {
  service: ServiceStatusBox;
}

export function ExpeditionQueueStatusBox({ service }: ExpeditionQueueStatusBoxProps) {
  const { expedition } = useSession();
  const role = expedition?.role;

  const status = service.key as QueueStatusKey;
  const acceptedKeys: ModalKey[] = ["attended", "missing", "dropout"];
  const hasAcceptedKey = acceptedKeys.includes(service.key as ModalKey);

  const isExpedition = role === Role.Expedicao;

  const handleModal = (type: ModalKey) => {
    const statusModal = MODAL_HANDLER.expeditions.queue.status[type];

    const match = new Map<ModalKey, typeof statusModal>([
      ["attended", statusModal],
      ["missing", statusModal],
      ["dropout", statusModal]
    ]);
    const keys = Array.from(match.keys());
    const handler = match.get(type);

    if (!handler) return console.log(`Modal type "${type}" is not supported, are accepted ${keys.join(", ")}.`);

    if (!isExpedition) return;

    handler({ type, title: service.title || "N/A" });
  };

  const handleClick = (type: ModalKey) => () => {
    if (!isExpedition) return;
    if (!hasAcceptedKey) return console.warn(`Service key "${service.key}" is not supported for modal opening. Only ${acceptedKeys.join(", ")} are allowed.`);
    handleModal(type);
  };

  const handleMouseEnter = () => {
    if (!isExpedition || !hasAcceptedKey) return;
    service.onMouseEnter?.();
  };

  return (
    <div
      className={cn(
        { hidden: !service.isVisible, block: service.isVisible, "cursor-pointer": hasAcceptedKey && isExpedition },
        { "pointer-events-none cursor-default": !isExpedition },
        "flex flex-col items-center justify-center gap-2"
      )}
      onClick={handleClick(status as ModalKey)}
      onMouseEnter={handleMouseEnter}
    >
      <div className={boxStyles({ status: status || "default" })}>
        <Text fw="bold">{service.value}</Text>
      </div>

      <Text ta="center" size="xs" c="gray.6" fw="bold" className={cn("max-w-min", { "min-w-[min(100%,50px)]!": service.key === "courtyard" })}>
        {service.title}
      </Text>
    </div>
  );
}

const boxStyles = cva({
  base: "flex w-full max-w-20 items-center justify-center rounded-md p-4 text-lg font-bold text-white",
  variants: {
    status: {
      scheduled: "bg-[#c4c4c4]",
      courtyard: "bg-[#4598c4]",
      attended: "bg-[#32a457]",
      missing: "bg-[#fc4040]",
      tons: "bg-[#c4c4c4]",
      dropout: "bg-[#3c3c3b]",
      default: "bg-[#c4c4c4]"
    },
    size: {
      sm: "p-1 text-sm",
      md: "text-md p-2",
      lg: "p-3 text-lg",
      xl: "p-4 text-xl"
    },
    radius: {
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      xl: "rounded-xl"
    }
  },
  defaultVariants: {
    size: "xl",
    radius: "xl",
    status: "default"
  }
});
